import { Component, ElementRef, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { Popover, PopoverModule } from 'primeng/popover';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { combineLatest } from 'rxjs';
import { AppConfigurator } from './app.configurator';
import { LayoutService } from '../service/layout.service';
import { AuthService } from '../../services/auth.service';
import { CommonService } from '../../services/common.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationInboxPanelComponent } from './notification-inbox-panel';
import { INotification } from '../../models/notification';
import { IAuthenticate } from '../../models/authenticate';
import { VmsProfileAvatarComponent } from '../../common/components/vms-profile-avatar';
import { VmsBrandLogoComponent } from '../../common/components/vms-brand-logo';
import { UserProfileService } from '../../services/user-profile.service';
import { ClientBrandingService } from '../../services/client-branding.service';
import { IUserMyProfile } from '../../models/user-profile';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [
        RouterModule,
        CommonModule,
        StyleClassModule,
        PopoverModule,
        BadgeModule,
        TooltipModule,
        ButtonModule,
        AppConfigurator,
        NotificationInboxPanelComponent,
        VmsProfileAvatarComponent,
        VmsBrandLogoComponent
    ],
    template: ` <div class="layout-topbar">
        <div class="layout-topbar-logo-container">
            <button
                class="layout-menu-button layout-topbar-action"
                (click)="layoutService.onMenuToggle()"
                pTooltip="Open menu"
                tooltipPosition="bottom"
                aria-label="Open menu"
            >
                <i class="pi pi-bars"></i>
            </button>
            <a class="layout-topbar-logo" routerLink="/" [attr.aria-label]="clientName || 'VMS home'">
                @if (clientLogo) {
                    <img class="layout-topbar-logo-img" [src]="clientLogo" [alt]="clientName || 'Client logo'" />
                } @else {
                <app-vms-brand-logo size="sm" class="layout-topbar-logo-svg" />
                }
                <span class="layout-topbar-client-name" [pTooltip]="clientName || 'VMS'" tooltipPosition="bottom">{{ clientName || 'VMS' }}</span>
            </a>
        </div>

        <div class="layout-topbar-actions">
            <div class="layout-config-menu">
                <button
                    type="button"
                    class="layout-topbar-action"
                    (click)="toggleDarkMode()"
                    [pTooltip]="darkModeTooltip"
                    tooltipPosition="bottom"
                    [attr.aria-label]="darkModeTooltip"
                >
                    <i [ngClass]="{ 'pi ': true, 'pi-moon': layoutService.isDarkTheme(), 'pi-sun': !layoutService.isDarkTheme() }"></i>
                </button>
                <div class="relative">
                    <button
                        class="layout-topbar-action layout-topbar-action-highlight"
                        pStyleClass="@next"
                        enterFromClass="hidden"
                        enterActiveClass="animate-scalein"
                        leaveToClass="hidden"
                        leaveActiveClass="animate-fadeout"
                        [hideOnOutsideClick]="true"
                        pTooltip="Theme and layout settings"
                        tooltipPosition="bottom"
                        aria-label="Theme and layout settings"
                    >
                        <i class="pi pi-palette"></i>
                    </button>
                    <app-configurator />
                </div>
            </div>

            <button
                class="layout-topbar-menu-button layout-topbar-action"
                pStyleClass="@next"
                enterFromClass="hidden"
                enterActiveClass="animate-scalein"
                leaveToClass="hidden"
                leaveActiveClass="animate-fadeout"
                [hideOnOutsideClick]="true"
                pTooltip="More actions"
                tooltipPosition="bottom"
                aria-label="More actions"
            >
                <i class="pi pi-ellipsis-v"></i>
            </button>

            <div class="layout-topbar-menu hidden lg:block">
                <div class="layout-topbar-menu-content">
                    <button
                        type="button"
                        class="layout-topbar-action"
                        (click)="onCalendarClick()"
                        pTooltip="View attendance calendar"
                        tooltipPosition="bottom"
                        aria-label="Attendance calendar"
                    >
                        <i class="pi pi-calendar"></i>
                        <span>Calendar</span>
                    </button>
                    <button
                        *ngIf="showMyPayslipAction"
                        type="button"
                        class="layout-topbar-action"
                        (click)="onMyPayslipClick()"
                        pTooltip="View your monthly payslip"
                        tooltipPosition="bottom"
                        aria-label="My Payslip"
                    >
                        <i class="pi pi-wallet"></i>
                        <span>My Payslip</span>
                    </button>
                    <button
                        *ngIf="showMyExpensesAction"
                        type="button"
                        class="layout-topbar-action"
                        (click)="onMyExpensesClick()"
                        pTooltip="Claim bus fare / petrol allowance"
                        tooltipPosition="bottom"
                        aria-label="My Expenses"
                    >
                        <i class="pi pi-car"></i>
                        <span>My Expenses</span>
                    </button>
                    <div class="topbar-popover-anchor">
                        <button
                            #notificationBtn
                            type="button"
                            class="layout-topbar-action notification-bell-btn"
                            [class.notification-bell-btn--unread]="hasUnreadNotifications"
                            [class.notification-bell-btn--pulse]="showUnreadPulse"
                            (click)="onNotificationsClick($event)"
                            pTooltip="Notifications inbox"
                            tooltipPosition="bottom"
                            aria-label="Notifications"
                        >
                            <i
                                class="pi pi-bell"
                                pBadge
                                [value]="notificationBadgeValue"
                                [badgeDisabled]="!hasUnreadNotifications"
                                severity="danger"
                                badgeSize="small"
                            ></i>
                            <span>Notifications</span>
                        </button>
                        <p-popover #notificationPopover appendTo="body" styleClass="notification-popover topbar-popover" [dismissable]="true">
                            <app-notification-inbox-panel
                                [notifications]="notifications"
                                [unreadCount]="unreadCount"
                                [loading]="notificationsLoading"
                                (markAllRead)="onMarkAllRead()"
                                (itemSelected)="onNotificationSelected($event)"
                            />
                        </p-popover>
                    </div>

                    <div class="topbar-popover-anchor">
                        <button
                            #profileBtn
                            type="button"
                            class="layout-topbar-action profile-topbar-btn"
                            (click)="onProfileClick($event)"
                            [pTooltip]="profileTooltip"
                            tooltipPosition="bottom"
                            aria-label="Profile"
                        >
                            <app-vms-profile-avatar
                                size="sm"
                                [photoUrl]="myProfile?.profilePhoto"
                                [gender]="myProfile?.gender"
                                [name]="displayName"
                            ></app-vms-profile-avatar>
                            <span class="profile-name-label">{{ displayName }}</span>
                        </button>
                        <p-popover #profilePopover appendTo="body" styleClass="profile-popover topbar-popover" [dismissable]="true">
                        <div class="profile-panel" *ngIf="currentUser">
                            <div class="profile-panel-head">
                                <app-vms-profile-avatar
                                    size="md"
                                    [photoUrl]="myProfile?.profilePhoto"
                                    [gender]="myProfile?.gender"
                                    [name]="displayName"
                                ></app-vms-profile-avatar>
                                <div class="profile-panel-identity">
                                    <div class="profile-panel-name">{{ displayName }}</div>
                                    <div class="profile-panel-username" *ngIf="currentUser.userName">&#64;{{ currentUser.userName }}</div>
                                    <span class="profile-role-tag">{{ roleLabel }}</span>
                                </div>
                            </div>

                            <div class="profile-panel-meta" *ngIf="clientName">
                                <i class="pi pi-building"></i>
                                <span>{{ clientName }}</span>
                            </div>

                            <div class="profile-panel-details" *ngIf="myProfile?.mobile || myProfile?.emergencyContactNo">
                                <div class="profile-detail-row" *ngIf="myProfile?.mobile">
                                    <i class="pi pi-mobile"></i>
                                    <span>{{ myProfile?.mobile }}</span>
                                </div>
                                <div class="profile-detail-row" *ngIf="myProfile?.emergencyContactNo">
                                    <i class="pi pi-phone"></i>
                                    <span>{{ myProfile?.emergencyContactNo }}</span>
                                </div>
                            </div>

                            <div class="profile-panel-section-label">Quick links</div>
                            <div class="profile-panel-links">
                                <button type="button" class="profile-link-btn" (click)="onCalendarClick(); profilePopover.hide()">
                                    <i class="pi pi-calendar"></i>
                                    <span>Attendance calendar</span>
                                </button>
                                <button
                                    *ngIf="showMyPayslipAction"
                                    type="button"
                                    class="profile-link-btn"
                                    (click)="onMyPayslipClick(); profilePopover.hide()"
                                >
                                    <i class="pi pi-wallet"></i>
                                    <span>My payslip</span>
                                </button>
                                <button
                                    *ngIf="showMyExpensesAction"
                                    type="button"
                                    class="profile-link-btn"
                                    (click)="onMyExpensesClick(); profilePopover.hide()"
                                >
                                    <i class="pi pi-car"></i>
                                    <span>My expenses</span>
                                </button>
                                <button
                                    *ngIf="showSettingsAction"
                                    type="button"
                                    class="profile-link-btn"
                                    (click)="onSettingsClick(); profilePopover.hide()"
                                >
                                    <i class="pi pi-cog"></i>
                                    <span>Settings</span>
                                </button>
                            </div>

                            <div class="profile-panel-footer">
                                <p-button
                                    label="Sign out"
                                    icon="pi pi-sign-out"
                                    severity="danger"
                                    [outlined]="true"
                                    styleClass="w-full"
                                    (onClick)="onLogoutClick(); profilePopover.hide()"
                                ></p-button>
                            </div>
                        </div>
                        </p-popover>
                    </div>
                </div>
            </div>
        </div>

    </div>`,
    styles: [
        `
            .notification-bell-btn {
                position: relative;
                overflow: visible;
            }

            .notification-bell-btn--unread i.pi-bell {
                color: var(--primary-color);
            }

            .notification-bell-btn--pulse i.pi-bell {
                animation: notification-bell-shake 0.55s ease-in-out;
            }

            @keyframes notification-bell-shake {
                0%,
                100% {
                    transform: rotate(0deg);
                }
                20% {
                    transform: rotate(14deg);
                }
                40% {
                    transform: rotate(-12deg);
                }
                60% {
                    transform: rotate(8deg);
                }
                80% {
                    transform: rotate(-6deg);
                }
            }

            .topbar-popover-anchor {
                display: inline-flex;
                align-items: center;
                position: relative;
            }

            :host ::ng-deep .topbar-popover-anchor > p-popover {
                position: absolute;
                width: 0;
                height: 0;
                overflow: hidden;
            }

            :host ::ng-deep .notification-popover.p-popover .p-popover-content,
            :host ::ng-deep .profile-popover.p-popover .p-popover-content {
                padding: 0;
            }

            :host ::ng-deep .topbar-popover.p-popover::before,
            :host ::ng-deep .topbar-popover.p-popover::after {
                display: none;
            }

            :host ::ng-deep .topbar-popover.p-popover {
                margin-top: 0.35rem;
            }

            .profile-topbar-btn {
                width: 2.5rem;
                height: 2.5rem;
                padding: 0;
                flex-shrink: 0;
                max-width: 11rem;
                gap: 0.45rem;
            }

            .layout-topbar-client-name {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 10rem;
            }

            .layout-topbar-logo-img {
                max-height: 2.5rem;
                max-width: 3rem;
                object-fit: contain;
            }

            .layout-topbar-logo-svg {
                height: 2.25rem;
                width: auto;
            }

            .profile-panel-details {
                display: flex;
                flex-direction: column;
                gap: 0.35rem;
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }

            .profile-detail-row {
                display: flex;
                align-items: center;
                gap: 0.45rem;
            }

            .profile-name-label {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 6.5rem;
            }

            .profile-panel {
                width: min(18rem, 88vw);
                padding: 1rem;
                display: flex;
                flex-direction: column;
                gap: 0.85rem;
            }

            .profile-panel-head {
                display: flex;
                align-items: center;
                gap: 0.75rem;
            }

            .profile-panel-identity {
                min-width: 0;
            }

            .profile-panel-name {
                font-weight: 700;
                font-size: 1rem;
                line-height: 1.3;
            }

            .profile-panel-username {
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }

            .profile-role-tag {
                display: inline-flex;
                margin-top: 0.35rem;
                padding: 0.15rem 0.5rem;
                border-radius: 999px;
                font-size: 0.6875rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.03em;
                background: var(--primary-50);
                color: var(--primary-700);
            }

            .profile-panel-meta {
                display: flex;
                align-items: center;
                gap: 0.45rem;
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
                padding: 0.55rem 0.65rem;
                border-radius: 0.5rem;
                background: var(--surface-100);
            }

            .profile-panel-section-label {
                font-size: 0.6875rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.04em;
                color: var(--text-color-secondary);
            }

            .profile-panel-links {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
            }

            .profile-link-btn {
                display: flex;
                align-items: center;
                gap: 0.55rem;
                width: 100%;
                border: none;
                background: transparent;
                color: var(--text-color);
                border-radius: 0.5rem;
                padding: 0.5rem 0.55rem;
                font-size: 0.875rem;
                cursor: pointer;
                text-align: left;
            }

            .profile-link-btn:hover {
                background: var(--surface-100);
            }

            .profile-panel-footer {
                padding-top: 0.25rem;
                border-top: 1px solid var(--surface-border);
            }
        `
    ]
})
export class AppTopbar {
    items!: MenuItem[];
    showSettingsAction = false;
    showMyPayslipAction = false;
    showMyExpensesAction = false;
    notifications: INotification[] = [];
    unreadCount = 0;
    notificationsLoading = false;
    showUnreadPulse = false;
    currentUser: IAuthenticate | null = null;
    clientName = '';
    clientLogo: string | null = null;
    myProfile: IUserMyProfile | null = null;

    @ViewChild('notificationPopover') notificationPopover?: Popover;
    @ViewChild('notificationBtn') notificationBtn?: ElementRef<HTMLButtonElement>;
    @ViewChild('profilePopover') profilePopover?: Popover;
    @ViewChild('profileBtn') profileBtn?: ElementRef<HTMLButtonElement>;

    private previousUnreadCount = 0;
    private loadedProfileForUserId: number | null = null;

    constructor(
        public layoutService: LayoutService,
        private router: Router,
        private authService: AuthService,
        private commonService: CommonService,
        private notificationService: NotificationService,
        private userProfileService: UserProfileService,
        private clientBrandingService: ClientBrandingService
    ) {
        combineLatest([this.authService.currentUser, this.commonService.menuCodesLoaded$]).subscribe(([user, menuCodesLoaded]) => {
            this.currentUser = user;
            this.clientName = this.commonService.clientDetail?.clientName ?? '';
            this.clientLogo = this.commonService.clientDetail?.clientLogo ?? null;
            this.clientBrandingService.setClientLogo(this.clientLogo);
            if (this.clientName) {
                this.clientBrandingService.applyBranding(this.clientName);
            }

            if (!user) {
                this.myProfile = null;
                this.loadedProfileForUserId = null;
            } else if (user.id !== this.loadedProfileForUserId) {
                this.loadMyProfile();
            }

            const roleId = user?.userRoleId;
            if (!roleId || !menuCodesLoaded) {
                this.showSettingsAction = false;
                this.showMyPayslipAction = false;
                this.showMyExpensesAction = false;
                return;
            }

            const hasSettingsCapability = this.hasMenuCapability('stngs') || this.hasMenuCapability('settings');
            this.showSettingsAction = roleId === 1 && hasSettingsCapability;
            this.showMyPayslipAction = [2, 3].includes(Number(roleId));
            this.showMyExpensesAction = [1, 2, 3].includes(Number(roleId));
        });

        if (!this.commonService.clientDetail) {
            this.commonService.loadClientDetail().subscribe({
                next: (data) => {
                    this.clientName = data?.clientName ?? '';
                    this.clientLogo = data?.clientLogo ?? null;
                    this.clientBrandingService.setClientLogo(this.clientLogo);
                    if (this.clientName) {
                        this.clientBrandingService.applyBranding(this.clientName);
                    }
                },
                error: () => {
                    this.clientName = '';
                    this.clientLogo = null;
                }
            });
        }

        this.notificationService.notifications$.subscribe((items) => {
            this.notifications = items;
        });

        this.notificationService.unreadCount$.subscribe((count) => {
            const nextCount = count ?? 0;
            if (nextCount > this.previousUnreadCount) {
                this.showUnreadPulse = true;
                window.setTimeout(() => {
                    this.showUnreadPulse = false;
                }, 700);
            }

            this.unreadCount = nextCount;
            this.previousUnreadCount = nextCount;
        });

        this.notificationService.liveNotification$.subscribe(() => {
            if (!this.notificationPopover?.overlayVisible) {
                this.showUnreadPulse = true;
                window.setTimeout(() => {
                    this.showUnreadPulse = false;
                }, 700);
            }
        });

        this.clientBrandingService.clientLogo$.subscribe((logo) => {
            this.clientLogo = logo;
        });
    }

    get displayName(): string {
        const user = this.currentUser;
        if (!user) {
            return 'Profile';
        }

        const fullName = [user.firstName, user.lastName].filter((part) => !!part?.trim()).join(' ').trim();
        return fullName || user.userName || 'Profile';
    }

    get userInitials(): string {
        const name = this.displayName;
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        }

        return name.slice(0, 2).toUpperCase();
    }

    get roleLabel(): string {
        return this.currentUser?.userRole || 'User';
    }

    get profileTooltip(): string {
        return `${this.displayName} (${this.roleLabel})`;
    }

    get darkModeTooltip(): string {
        return this.layoutService.isDarkTheme() ? 'Switch to light mode' : 'Switch to dark mode';
    }

    get hasUnreadNotifications(): boolean {
        return this.effectiveUnreadCount > 0;
    }

    get notificationBadgeValue(): string {
        const count = this.effectiveUnreadCount;
        if (count <= 0) {
            return '';
        }

        return count > 99 ? '99+' : String(count);
    }

    private get effectiveUnreadCount(): number {
        if (this.unreadCount > 0) {
            return this.unreadCount;
        }

        return this.notifications.filter((item) => !item.isRead).length;
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }

    onCalendarClick() {
        this.router.navigate(['/pages/employee/emp-attendance']);
    }

    onMyPayslipClick() {
        this.router.navigate(['/pages/employee/emp-payslips']);
    }

    onMyExpensesClick() {
        this.router.navigate(['/pages/employee/my-expenses']);
    }

    onProfileClick(event: Event): void {
        this.notificationPopover?.hide();
        const target = this.profileBtn?.nativeElement ?? event.currentTarget;
        this.profilePopover?.toggle(event, target);
    }

    onNotificationsClick(event: Event): void {
        this.profilePopover?.hide();
        const target = this.notificationBtn?.nativeElement ?? event.currentTarget;
        this.notificationPopover?.toggle(event, target);
        void this.loadNotificationInbox();
    }

    private async loadNotificationInbox(): Promise<void> {
        this.notificationsLoading = true;
        try {
            await this.notificationService.refreshInbox();
        } finally {
            this.notificationsLoading = false;
        }
    }

    async onMarkAllRead() {
        await this.notificationService.markAllRead();
    }

    async onNotificationSelected(item: INotification) {
        if (!item.isRead) {
            await this.notificationService.markRead([item.userNotificationId]);
        }
        this.notificationPopover?.hide();
    }

    onLogoutClick() {
        this.authService.logout();
    }

    onSettingsClick() {
        this.router.navigate(['/pages/settings']);
    }

    private loadMyProfile(): void {
        const userId = this.currentUser?.id ?? null;
        this.userProfileService.getMyProfile().subscribe({
            next: (profile) => {
                this.myProfile = profile;
                this.loadedProfileForUserId = userId;
            },
            error: () => (this.myProfile = null)
        });
    }

    private hasMenuCapability(code: string): boolean {
        return this.commonService.menuCodes?.some((item) => String(item.value ?? '').toLowerCase() === code.toLowerCase()) ?? false;
    }
}
